"use client";

import { useAuth } from "../hooks/useAuth";
import { useSession } from "next-auth/react";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export function ProtectedRoute({ children }: ProtectedRouteProps) {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { data: session, status: sessionStatus } = useSession();
  const router = useRouter();
  const [initialCheckDone, setInitialCheckDone] = useState(false);

  // NextAuth 세션이 있으면 Firebase Auth 대신 사용
  const isUserAuthenticated = user || session?.user;
  const isUserLoading = isLoading || sessionStatus === "loading";

  // 초기 로드 후 약간의 딜레이를 주어 세션이 완전히 로드되도록 함
  useEffect(() => {
    const timer = setTimeout(() => {
      setInitialCheckDone(true);
    }, 500); // 500ms 딜레이

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // 초기 체크가 완료되고, 로딩 중이 아니고, 사용자가 없을 때만 리다이렉트
    // 단, 이미 로그인 페이지에 있으면 리다이렉트하지 않음
    if (
      initialCheckDone &&
      !isUserLoading &&
      !isUserAuthenticated &&
      typeof window !== "undefined" &&
      !window.location.pathname.startsWith("/auth/")
    ) {
      // 현재 페이지 URL을 callbackUrl로 전달
      const currentPath = window.location.pathname + window.location.search;
      router.push(`/auth/login?callbackUrl=${encodeURIComponent(currentPath)}`);
    }
  }, [initialCheckDone, isUserLoading, isUserAuthenticated, router]);

  // 초기 체크가 완료되지 않았거나 로딩 중이면 로딩 표시
  if (!initialCheckDone || isUserLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  if (!isUserAuthenticated) {
    return null;
  }

  return <>{children}</>;
}
